import React, { useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import moment from "moment"

import { DialogM } from "@common/components/alert/dialog/Dialog"
import FormStepperModal from "@common/components/stepper/FormStepperModal"
import { apiIdle } from "@data/redux/actions/api"
import { subscriptionService } from "@data/services/subscriptionService"

import { IndividualDatesStep } from "./parentExtraHours/IndividualDatesStep"
import { ParametersStep } from "./parentExtraHours/ParametersStep"
import { RecapStep } from "./parentExtraHours/RecapStep"
import { SharedSlotsStep } from "./parentExtraHours/SharedSlotsStep"
import { DATETIME_FORMAT, slotStringToApi } from "./parentExtraHours/utils"

/**
 * Step 2 — slot picker. Branches on format:
 *  - shared     → recurring weekly grid. Parent flags every disponibility
 *                 so the auto-routing service has the widest possible
 *                 matching pool.
 *  - individual → N exact (date + time) picks. Auto-routing is skipped
 *                 server-side; ops schedules the session manually.
 */
const SlotsStep = (props) => {
    const format = props.formValues?.format ?? "shared"
    return format === "individual" ? (
        <IndividualDatesStep {...props} />
    ) : (
        <SharedSlotsStep {...props} />
    )
}

const ParentExtraHoursModal = (props) => {
    const {
        visible,
        setVisible,
        onSuccess,
        user,
        schoolSubjects,
        extraHourPricePerHour,
    } = props

    // Start with an empty slot pool — the parent's existing
    // availabilities are about their *regular* lives and shouldn't be
    // assumed as preferred for an extra hour. Forcing a fresh pick
    // keeps the request honest to what the parent actually wants now.
    const defaultFormValues = useMemo(
        () => ({
            hours: 1,
            deadline_type: "7_days",
            custom_deadline: null,
            format: "shared",
            subject_id: null,
            selected_slots: [],
            // Individual flow: one ISO "YYYY-MM-DD HH:mm" per requested
            // hour. Filled in step 2 only when format === "individual".
            requested_dates: [],
        }),
        []
    )

    const [formValues, setFormValues] = useState(defaultFormValues)

    useEffect(() => {
        if (visible) {
            setFormValues(defaultFormValues)
            // Clear any 422 left over from a previous submission so the
            // recap doesn't open with a stale error already painted by
            // FormStep's last-step `serverError` block.
            if (props.apiError) {
                props.apiIdle(subscriptionService.createExtraHourRequest)
            }
        }
    }, [visible])

    const transformSubmitData = () => {
        const isIndividual = formValues?.format === "individual"
        const base = {
            hours: formValues?.hours,
            format: formValues?.format,
            deadline_type: formValues?.deadline_type,
            custom_deadline:
                formValues?.deadline_type === "custom" &&
                formValues?.custom_deadline
                    ? moment(formValues.custom_deadline).format("YYYY-MM-DD")
                    : null,
            subject_id: formValues?.subject_id,
        }

        if (isIndividual) {
            // Backend reads `requested_dates` and short-circuits the
            // auto-routing pipeline — ops will schedule each session
            // manually against the parent's exact pick.
            return {
                ...base,
                requested_dates: (formValues?.requested_dates ?? []).filter(
                    (v) =>
                        typeof v === "string" &&
                        moment(v, DATETIME_FORMAT, true).isValid()
                ),
            }
        }

        return {
            ...base,
            preferred_slots: (formValues?.selected_slots ?? [])
                .map(slotStringToApi)
                .filter(Boolean),
        }
    }

    // `defaultValue: null` is load-bearing: FormStep's mount effect calls
    // `setFormValues({...control._formValues, [name]: data.defaultValue})`
    // whenever `data.defaultValue !== field.value`. On first mount that
    // wipes our real form state (hours, format, deadline_type…) because
    // RHF's _formValues still only holds the step-name keys. Matching
    // field.value (which RHF initializes to null) makes the effect a
    // no-op so the state we seed via useState survives the first render.
    const formData = [
        {
            type: "custom",
            name: "parameters",
            defaultValue: null,
            component: ParametersStep,
            componentProps: { schoolSubjects, user, extraHourPricePerHour },
            showNextButton: false,
            showSubmitButton: false,
        },
        {
            type: "custom",
            name: "slots",
            defaultValue: null,
            component: SlotsStep,
            componentProps: { user },
            showNextButton: false,
            showSubmitButton: false,
        },
        {
            type: "custom",
            name: "recap",
            defaultValue: null,
            component: RecapStep,
            componentProps: { schoolSubjects, extraHourPricePerHour },
            showNextButton: false,
            showSubmitButton: false,
        },
    ]

    return (
        <FormStepperModal
            visible={visible}
            setVisible={setVisible}
            track={false}
            formData={formData}
            formValues={formValues}
            setFormValues={setFormValues}
            defaultValues={defaultFormValues}
            submitService={subscriptionService.createExtraHourRequest}
            submitButtonText="Confirmer la demande"
            transformSubmitData={transformSubmitData}
            submitSuccessCallback={() => {
                setVisible(false)
                DialogM.show({
                    text1: "Demande envoyée",
                    text2: "Nous revenons vers vous dès que la demande a été traitée.",
                    exitButton: true,
                    onPressBack: () => {},
                })
                if (onSuccess) onSuccess()
            }}
            syncFormValues
        />
    )
}

const mapStateToProps = (state) => ({
    user: state.data.currentUser?.object,
    isLoading: state.api.createExtraHourRequest?.request,
    apiError: state.api.createExtraHourRequest?.error,
    schoolSubjects: state.data.formData?.schoolSubjects ?? [],
    // Tarif catalogue des heures sup, servi par le backend
    // (`ExtraHourRequest::PRICE_PER_HOUR`). Les steps retombent sur leur miroir
    // local si staticData n'est pas encore chargée.
    extraHourPricePerHour: state.data.staticData?.extra_hour_price_per_hour,
})

const mapDispatchToProps = { apiIdle }

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ParentExtraHoursModal)
